import * as d3 from "d3";
import MitMapSelectionService from './MitMapSelectionService.js';
import MitNavDrawingService from './MitNavDrawingService.js';

class MitMapService {
    constructor(svgId, objectDefinitions, selectChangedCallback, highlightChangedCallback, editObjectCallback) {
        this.svg = d3.select(svgId);
        this.data = null;

        this.EDIT_OBJECT_TYPE = { CREATED:'CREATED', MODIFIED:'MODIFIED', DELETED:'DELETED' }
        this.editObjectCallback = editObjectCallback;
        this.serviceSelection = new MitMapSelectionService(selectChangedCallback, highlightChangedCallback);
        // Rooms are styled by the selection overlay below; the selection service only tracks them
        this.serviceSelection.skipUIFor = (space) => this.isSpaceFeature(space);
        this.navService = new MitNavDrawingService(this);

        this.objectDefinitions = objectDefinitions;
        this.objectToCreate = null;
        this.editmode = false;
        this.editModeAllowModify = false;

        this.roomNumberOptions = { 
            enabled: false, 
            size: 4, 
            removeBg: false, 
            bold: false, 
            color: '#000000' 
        };

        this.projection = null;
        this.path = null;
        this.zoom = null;

        this.layerMap = null;
        this.layerPrevLevel = null;
        this.layerCurrentLevel = null;
        this.layerObjects = null;
        this.layerNavLines = null;
        this.layerNavPoints = null;
        this.layerActiveRoute = null;

        this.selectedFloor = null;
        this.selected = {};
        this.groups = [];
        
        this.mapType = 'Default';
        this.showAllObjects = false;
        this.showNeighborhoods = false; 
        this.showConferenceRooms = false; 
        // this.CONFERENCE_COLOR = '#8E44AD'; // Purple for conference rooms
        // this.CONFERENCE_COLOR = '#3458eb'; // Purple for conference rooms
        // this.CONFERENCE_COLOR = '#000000'; // Purple for conference rooms
        // this.CONFERENCE_SHOW_GROUP_DIM = .8
        this.CONFERENCE_COLOR = '#cbcaca'; 
        this.CONFERENCE_SHOW_GROUP_DIM = .9
        this.dimNeighborhoodsForConference = false;

        // Selection is drawn as an overlay on top of the base colors so it never fights them
        this.SELECTION_COLOR = '#1F3A68';
        this.SELECTION_DIM_OPACITY = 0.35;
        this.selection = { kind: null, ids: new Set(), primaryId: null };
        this.lastSelectionKey = null;
        this.dimUnselected = true;
        this.currentSpaces = null;
        this.currentObjects = null;

        this.colorCodes = {
            'BACKGROUND':'#DDC7A8', 'GRAY_LIGHT':'#F0F0F0', 'GRAY_DARK': '#E2E2E2',
            'CORRIDOR':'#E5D5BE', 'STAIRWAY':'#A5A5A4', 'ELEVATOR':'#A5A5A4',
        };
    }

    drawCompass(geojson) {
        // 1. Remove any existing compass
        this.layerMap.select(".compass-rose").remove();

        // 2. Calculate the exact bounding box of the current floor plan
        const bounds = this.path.bounds(geojson);
        // bounds[0] is the top-left [minX, minY]
        // bounds[1] is the bottom-right [maxX, maxY]

        // 3. Set to the bottom-right corner of the building with extra buffer
        // bounds[1][0] is the far right edge, bounds[1][1] is the absolute bottom.
        const bufferX = 100;  // Pushes it slightly past the right edge
        const bufferY = -680;  // Increased buffer to keep it further below the structures
        
        const xPos = bounds[1][0] + bufferX; 
        const yPos = bounds[1][1] + bufferY; 

        // 4. Append to the zoomable map layer and apply opacity to fade it
        const compassGroup = this.layerMap.append("g")
            .attr("class", "compass-rose")
            .attr("transform", `translate(${xPos}, ${yPos})`)
            .style("opacity", 0.7); // <-- Fades the entire compass

        // 5. Calculate True North based on the projection
        const centerLngLat = [-71.0901, 42.3622]; 
        const northLngLat = [-71.0901, 42.3622 + 0.1]; 

        const pCenter = this.projection(centerLngLat);
        const pNorth = this.projection(northLngLat);

        const angleRad = Math.atan2(pNorth[1] - pCenter[1], pNorth[0] - pCenter[0]);
        const angleDeg = (angleRad * (180 / Math.PI)) + 90;

        // 6. Apply rotation to the inner icon group
        const compassIcon = compassGroup.append("g")
            .attr("transform", `rotate(${angleDeg})`);

        // 7. Draw the all-black North-pointing arrow
        compassIcon.append("path")
            .attr("d", "M0,-20 L10,15 L0,8 L-10,15 Z")
            .attr("fill", "#888585") 
            .attr("stroke", "none"); 

        // 8. Add the all-black 'N' label
        compassIcon.append("text")
            .attr("x", 0)
            .attr("y", -25)
            .attr("text-anchor", "middle")
            .attr("font-size", "14px")
            .attr("font-weight", "bold")
            .attr("fill", "#000000")
            .text("N");
    }

    setRoomNumberDisplayOptions(options) {
        if (options) {
            this.roomNumberOptions = options;
        }
    }

    setMapDisplayOptions(mapType, showAllObjects, showNeighborhoods, showConferenceRooms = false) {
        this.mapType = mapType || 'Default';
        this.showAllObjects = showAllObjects || false;
        this.showNeighborhoods = showNeighborhoods || false; 
        this.showConferenceRooms = showConferenceRooms;
    }

    setGroups(groups) { this.groups = groups; }
    setHighlightMouseOver(highlight){ this.highlight = highlight; }
    setEditMode(editmode){ this.editmode = editmode; }
    
    setEditModelAllowModify(allow, existingObjectMitIdsList, existingNavMitIdsList){
        this.editModeAllowModify = allow;
        this.existingObjectMitIdsList = existingObjectMitIdsList;
        this.existingNavMitIdsList = existingNavMitIdsList || {}; 
    }
    
    deselect(){
        let current = this.serviceSelection.currentlySelected;
        if(current && current.space) {
            this.serviceSelection.unselect(current.space, current.d3Element);
        }
    }

    // toggle=false keeps an already-selected item selected (used for search/URL/legend)
    select(mitId, toggle = true){
        let item = d3.select("#mit_" + mitId);
        if(item.empty()) return;

        let space = item.data()[0];
        let current = this.serviceSelection.currentlySelected;
        if(!toggle && current && current.space === space){
            current.d3Element = item;
            this.serviceSelection.selectEvent(space, item);
            return;
        }
        this.serviceSelection.select(space, item);
    }

    isSpaceFeature(feature){
        return !!(feature && feature.properties && feature.properties.spaceType === 'space');
    }

    setSelectionDimming(dim){ this.dimUnselected = dim; }

    // Selection precedence: group, then single space/object, then use type
    buildSelection(selectedUseTypes, selectedSpaceMitId, selectedGroupId){
        let selection = { kind: null, ids: new Set(), primaryId: null };

        let group = selectedGroupId ? (this.groups || []).find(g => g.id === selectedGroupId) : null;
        if (group) {
            selection.kind = 'group';
            selection.ids = new Set(group.spaceIds || []);
            selection.primaryId = group.headSpaceId || null;
        } else if (selectedSpaceMitId) {
            selection.kind = 'space';
            selection.ids = new Set([selectedSpaceMitId]);
            selection.primaryId = selectedSpaceMitId;
        } else if (selectedUseTypes) {
            selection.kind = 'type';
            [this.currentSpaces, this.currentObjects].forEach(model => {
                if (!model || !model.features) return;
                model.features.forEach(f => {
                    if (f.properties && f.properties.useType == selectedUseTypes) selection.ids.add(f.properties.mitId);
                });
            });
        }
        return selection;
    }

    // Update the selection without redrawing the map
    setSelection(selectedUseTypes, selectedSpaceMitId, selectedGroupId){
        this.selectedGroupId = selectedGroupId;
        this.selection = this.buildSelection(selectedUseTypes, selectedSpaceMitId, selectedGroupId);
        this.applySelection();
    }

    getSelectableNodes(ids){
        let nodes = [];
        if (!ids || ids.size === 0) return nodes;
        [this.layerSpaces, this.layerObjects].forEach(layer => {
            if (!layer) return;
            layer.selectAll("path").each(function(d){
                if (d && d.properties && ids.has(d.properties.mitId) && this.style.display !== 'none') {
                    nodes.push({ feature: d, element: this });
                }
            });
        });
        return nodes;
    }

    appendOutline(layer, element, strokeWidth, fillOpacity, glow = false){
        let g = layer.append("g")
            .attr("transform", element.getAttribute("transform"))
            .style("pointer-events", "none");
        let d = element.getAttribute("d");

        // Soft wide ring around the shape so small rooms/objects are easy to spot at any zoom
        if (glow) {
            g.append("path")
                .attr("d", d)
                .attr("fill", "none")
                .attr("stroke", this.SELECTION_COLOR)
                .attr("stroke-opacity", 0.25)
                .attr("stroke-width", strokeWidth + 12)
                .attr("stroke-linejoin", "round")
                .attr("vector-effect", "non-scaling-stroke");
        }

        // White halo underneath keeps the outline readable on any fill
        g.append("path")
            .attr("d", d)
            .attr("fill", "none")
            .attr("stroke", "#ffffff")
            .attr("stroke-width", strokeWidth + 3)
            .attr("stroke-linejoin", "round")
            .attr("vector-effect", "non-scaling-stroke");

        return g.append("path")
            .attr("d", d)
            .attr("fill", this.SELECTION_COLOR)
            .attr("fill-opacity", fillOpacity)
            .attr("stroke", this.SELECTION_COLOR)
            .attr("stroke-width", strokeWidth)
            .attr("stroke-linejoin", "round")
            .attr("vector-effect", "non-scaling-stroke");
    }

    applySelection(){
        if (!this.layerSpaces || !this.layerSelection) return;

        let sel = this.selection;
        let nodes = this.getSelectableNodes(sel.ids);
        let dim = this.dimUnselected && nodes.length > 0;

        this.layerSpaces.selectAll("path").style("opacity", (f) => {
            let base = this.getBaseSpaceOpacity(f);
            if (dim && !sel.ids.has(f.properties.mitId)) return Math.min(base, this.SELECTION_DIM_OPACITY);
            return base;
        });

        this.layerSelection.selectAll("*").remove();

        let key = sel.kind ? sel.kind + '|' + sel.primaryId + '|' + Array.from(sel.ids).sort().join(',') : null;
        let isNew = key !== null && key !== this.lastSelectionKey;
        this.lastSelectionKey = key;

        nodes.forEach(({ feature, element }) => {
            let isSingle = sel.kind === 'space';
            let isPrimary = sel.kind === 'group' && feature.properties.mitId === sel.primaryId;
            let width = sel.kind === 'type' ? 2.5 : ((isPrimary || isSingle) ? 4.5 : 3);
            let fillOpacity = isSingle ? 0.32 : (isPrimary ? 0.28 : 0.16);
            let outline = this.appendOutline(this.layerSelection, element, width, fillOpacity, isSingle);

            // Pulse when the selection changes so the eye finds it (twice for a single selection)
            if (isNew) {
                let pulse = outline.attr("stroke-width", width + 6)
                    .transition().duration(700).ease(d3.easeCubicOut)
                    .attr("stroke-width", width);
                if (isSingle) {
                    pulse.transition().duration(350).ease(d3.easeCubicOut).attr("stroke-width", width + 6)
                        .transition().duration(700).ease(d3.easeCubicOut).attr("stroke-width", width);
                }
            }
        });
    }

    // Thin outline used for hover and legend previews; null clears it
    previewIds(ids){
        if (!this.layerHover) return;
        this.layerHover.selectAll("*").remove();
        if (!ids || ids.length === 0) return;

        let idSet = new Set(ids.filter(id => !this.selection.ids.has(id)));
        this.getSelectableNodes(idSet).forEach(({ element }) => {
            this.appendOutline(this.layerHover, element, 1.5, 0.08).attr("stroke-opacity", 0.85);
        });
    }

    // Gentle zoom: keep surrounding context visible, groups and types zoom least
    zoomToSelection(){
        let options = {
            space: { maxScale: 2.5, fill: 0.35 },
            group: { maxScale: 1.6, fill: 0.6 },
            type:  { maxScale: 1.3, fill: 0.8 }
        }[this.selection.kind];
        this.zoomToFeatures(this.getSelectableNodes(this.selection.ids).map(n => n.feature), options);
    }

    changeCreateObject(obj){
        this.objectToCreate = obj;
        if(this.objectToCreate){
            this.svg.style("cursor", "crosshair");
        } else {
            this.svg.style("cursor", "default");
            this.navService.cancelNavDrawing();
        }
    }

    createGeojsonModel(){ return { type: "FeatureCollection", features: [] }; }
    
    deleteSelectedObject(){
        let selected = this.serviceSelection.currentlySelected;
        if (selected && selected.d3Element) {
            selected.d3Element.remove();
            this.editObjectCallback(this.EDIT_OBJECT_TYPE.DELETED, selected.space, selected.d3Element);
            if (selected.space.properties.spaceType === 'nav_point') {
                this.layerNavLines.selectAll("path").filter(d => {
                    return d.properties.startPointId === selected.space.properties.mitId || 
                           d.properties.endPointId === selected.space.properties.mitId;
                }).each((d, i, nodes) => {
                    let elem = d3.select(nodes[i]);
                    elem.remove();
                    this.editObjectCallback(this.EDIT_OBJECT_TYPE.DELETED, d, elem);
                });
            }
        }
    }

    rotateSelectedObject(angle){
        let s = this.serviceSelection.currentlySelected.space;
        if(s && s.properties && s.properties.spaceType === 'object'){
            let d = this.serviceSelection.currentlySelected.d3Element;
            let centerPoint = this.path.centroid(this.serviceSelection.currentlySelected.space);
            d.attr("transform", "rotate(" + angle + ", " + centerPoint[0] + ", " + centerPoint[1] + ")");
            this.editObjectCallback(this.EDIT_OBJECT_TYPE.MODIFIED, this.serviceSelection.currentlySelected.space, this.serviceSelection.currentlySelected.d3Element);
        }
    }

    getObjectScreenSize(space){
        let p1 = this.projection(space.geometry.coordinates[0][0]);
        let p2 = this.projection(space.geometry.coordinates[0][1]);
        let p3 = this.projection(space.geometry.coordinates[0][2]);
        let p4 = this.projection(space.geometry.coordinates[0][3]);
        return {
            width:Math.sqrt( Math.pow((p3[0] - p2[0]),2) + Math.pow((p3[1] - p2[1]),2) ),
            height:Math.sqrt( Math.pow((p2[0] - p1[0]),2) + Math.pow((p2[1] - p1[1]),2) )
        };
    }

    changeSizeSelectedObject(amount){
        let s = this.serviceSelection.currentlySelected.space;
        let d = this.serviceSelection.currentlySelected.d3Element;
        let centerPoint = this.path.centroid(s);
        let size = this.getObjectScreenSize(s);

        size.width = size.width * amount;
        size.height = size.height * amount;

        if(s && s.properties && s.properties.spaceType === 'object' & size.width > 3 && size.width < 65 && size.height > 3 && size.height < 65){
            let p1 = this.projection.invert([centerPoint[0]-(size.width/2),centerPoint[1]-(size.height/2)]);
            let p2 = this.projection.invert([centerPoint[0]-(size.width/2),(centerPoint[1]-(size.height/2))+size.height]);
            let p3 = this.projection.invert([centerPoint[0]-(size.width/2)+size.width,(centerPoint[1]-(size.height/2))+size.height]);
            let p4 = this.projection.invert([centerPoint[0]-(size.width/2)+size.width,centerPoint[1]-(size.height/2)]);

            s.geometry.coordinates = [[ p1,p2,p3,p4,p1]];
            s.properties.geometryCoordinates = [[p1,p2,p3,p4,p1]];

            d.datum(s).attr("d", (s) => this.path(s))
                      .style('background-size', size.width + 'px' + ' ' + size.height + 'px');

            this.editObjectCallback(this.EDIT_OBJECT_TYPE.MODIFIED, this.serviceSelection.currentlySelected.space, this.serviceSelection.currentlySelected.d3Element);
        }
    }

    checkObjectNameValid(list, id){ return !list.hasOwnProperty(id); }
    makeNameSafeForId(name){ return name.replace(/[^a-zA-Z0-9]/g, "").replace(' ', '').toLowerCase(); }

    getObjectInfo(existingMitIdList, buildingMitId, objectUseType, objectName) {
        const navTypes = ['nav_point', 'nav_line', 'PATH', 'LEVEL_CHANGE', 'END_POINT', 'CONNECTION_POINT', 'QR_POINT', 'PATH_NODE'];
        
        if (navTypes.includes(objectUseType)) {
            const uuid = crypto.randomUUID();
            let finalName = objectName || objectUseType;
            if (objectUseType === 'END_POINT' || objectUseType === 'LEVEL_CHANGE') {
                finalName = ''; 
            }

            return {
                mitId: `${buildingMitId}_${objectUseType}_${uuid}`,
                mitIdShort: `${objectUseType}_${uuid}`,
                name: finalName
            };
        }

        let info = { mitId: buildingMitId + "-" + objectUseType, mitIdShort: objectUseType, name: objectName };
        let nextId = 1;
        while (this.checkObjectNameValid(existingMitIdList, info.mitId + nextId) == false) { nextId++; }
        
        info.mitId = info.mitId + nextId; 
        info.mitIdShort = info.mitIdShort + nextId; 
        info.name = info.name + nextId;
        return info;
    }

    addObject(objToCreate, mouseX, mouseY, parentSpace){
        if(!this.editModeAllowModify) return;

        let gisPoint = this.projection.invert([mouseX, mouseY]);

        if (objToCreate.type === 'END_POINT' || objToCreate.type === 'LEVEL_CHANGE' || objToCreate.type === 'QR_POINT') {
            this.navService.drawNavPoint(objToCreate, gisPoint, parentSpace);
            return;
        } else if (objToCreate.type === 'PATH') {
            if (this.navService.navDrawingState.active) {
                this.navService.navDrawingState.points.push(gisPoint);
                this.navService.updateTempNavLine();
            }
            return;
        }

        let p1 = this.projection.invert([mouseX-(objToCreate.width/2),mouseY-(objToCreate.height/2)]);
        let p2 = this.projection.invert([mouseX-(objToCreate.width/2),(mouseY-(objToCreate.height/2))+objToCreate.height]);
        let p3 = this.projection.invert([mouseX-(objToCreate.width/2)+objToCreate.width,(mouseY-(objToCreate.height/2))+objToCreate.height]);
        let p4 = this.projection.invert([mouseX-(objToCreate.width/2)+objToCreate.width,mouseY-(objToCreate.height/2)]);

        let objInfo = this.getObjectInfo(this.existingObjectMitIdsList, parentSpace.properties.mitBuildingId, objToCreate.type, objToCreate.name)
        let feature = {
                type: "Feature",
                geometry: { type: "Polygon", coordinates: [] },
                properties:{
                    mitId:objInfo.mitId, mitIdShort:objInfo.mitIdShort, mitBuildingId:parentSpace.properties.mitBuildingId,
                    name:objInfo.name, description:'', level:parentSpace.properties.level, sqfoot:null, org:'',
                    useType:objToCreate.type, useCategory:objToCreate.type+'S', rotation:0, spaceType:'object',
                    version: parentSpace.properties.version, parentId: parentSpace.properties.id,
                    geometryCoordinates:[], geometryType:'Polygon'
                }
        };
        feature.geometry.coordinates.push([ p1,p2,p3,p4,p1]);
        feature.properties.geometryCoordinates.push([ p1,p2,p3,p4,p1]);

        let createdOject = this.layerObjects.append("path");

            createdOject.datum(feature)
                        .attr("d", (feature) => this.path(feature))
                        .attr("fill", "url(#"+objToCreate.type.replace(/ /g,"_")+")")
                        .attr("cursor", "grab")
                        .on("click", (event, obj) => {
                            if(this.editmode) this.serviceSelection.select(obj, d3.select(event.target));
                            else this.serviceSelection.selectEvent(obj, d3.select(event.target));
                        })
                        .on("mouseover", (event, obj) => {
                            if(!this.editmode) this.serviceSelection.highlight(obj, d3.select(event.target));
                        })
                        .on("mouseleave", (event, obj) => {
                            if(!this.editmode) this.serviceSelection.highlight(obj, d3.select(event.target));
                        });

            createdOject.call(d3.drag()
                .on("start", (event, space) => this.dragStartEvent(event,space))
                .on("drag", (event, space) => this.dragEvent(event,space))
                .on("end", (event, space) => this.dragEndEvent(event,space))
            );

            this.serviceSelection.select(feature, createdOject);
            this.editObjectCallback(this.EDIT_OBJECT_TYPE.CREATED, feature, createdOject);
    }

    convertGeoJSONtoMapModel(geoJSON){
        let mapModel = {};
        let level,spaceType;
        let levels = [];

        for(let x = 0; x < geoJSON.features.length; x++){
            level = geoJSON.features[x].properties.level;
            levels.push(level);
            spaceType = geoJSON.features[x].properties.spaceType;

            if(mapModel.hasOwnProperty(spaceType) === false){ mapModel[spaceType] = {}; }
            if(mapModel[spaceType].hasOwnProperty(level) === false){
                mapModel[spaceType][level] = { "type": "FeatureCollection", "features": [] };
            }
            mapModel[spaceType][level].features.push(geoJSON.features[x]);
        }

        for(let x = 0; x < levels.length; x++){
            if(mapModel.hasOwnProperty('object') === false) mapModel['object'] = {};
            if(mapModel['object'].hasOwnProperty(levels[x]) === false){
                mapModel['object'][levels[x]] = { "type": "FeatureCollection", "features": [] };
            }
        }
        return mapModel;
    }

    enableZoom(zoomInElementSelector, zoomOutElementSelector, zoomResetElementSelector){
        this.zoomControls = {
            in:zoomInElementSelector,
            out:zoomOutElementSelector,
            reset:zoomResetElementSelector
        }

        this.zoom = d3.zoom().scaleExtent([.3, 10]).on("zoom", function (e) {
            d3.select('svg #group-map').attr('transform',e.transform);
        });

        this.svg.call(this.zoom).on("wheel.zoom", null);
        this.addZoomEvents();
        this.navService.setupGlobalEvents();
    }

    createMap(spacesModel, objectsModel, doorsModel, windowsModel, structuresModel, floorDict, width, height, selectedFloor, selectedUseTypes, selectedSpaceMitId, selectedOrg, navFeaturesModel, selectedGroupId){
        this.clearMap();
        this.selectedFloor = selectedFloor;
        this.selectedGroupId = selectedGroupId;
        this.svg.attr("width", '100%').attr("height", '100vh');
        this.addUIObjectsDefinitions(this.objectDefinitions);
        this.createProjection(spacesModel[selectedFloor], width, height);
        this.createLayers();
        this.currentFloor = floorDict[selectedFloor];
        
        if(spacesModel){ this.renderLayerSpaces(spacesModel[selectedFloor], selectedFloor, selectedUseTypes, selectedSpaceMitId, selectedOrg); }
        
        if(objectsModel){
            if(typeof objectsModel[selectedFloor] === 'undefined' || objectsModel[selectedFloor] === null){
                objectsModel[selectedFloor] = this.createGeojsonModel();
            }
            this.renderLayerObjects(objectsModel[selectedFloor], selectedFloor, selectedUseTypes, selectedSpaceMitId, selectedOrg);
        }
        
        if(structuresModel && this.mapType === 'Structures'){
            if (structuresModel[selectedFloor] && structuresModel[selectedFloor][0]) {
                this.renderLayerStructures(structuresModel[selectedFloor][0], selectedFloor);
            }
        }
        
        if(windowsModel){ this.renderLayerWindows(windowsModel[selectedFloor], selectedFloor, selectedUseTypes, selectedSpaceMitId, selectedOrg); }
        if(doorsModel){ this.renderLayerDoors(doorsModel[selectedFloor], selectedFloor, selectedUseTypes, selectedSpaceMitId, selectedOrg); }
        
        if (navFeaturesModel) {
            let filteredNav = navFeaturesModel.filter(f => {
                if (f.properties.useType === 'LEVEL_CHANGE' && f.properties.accessToLevels) {
                    return f.properties.accessToLevels.map(String).includes(String(selectedFloor));
                }
                return f.properties.level == selectedFloor;
            });
            this.navService.renderExistingNavData(filteredNav);
        }

        if (spacesModel && spacesModel[selectedFloor]) {
            this.drawCompass(spacesModel[selectedFloor]);
        }

        this.currentSpaces = spacesModel ? spacesModel[selectedFloor] : null;
        this.currentObjects = objectsModel ? objectsModel[selectedFloor] : null;
        this.selection = this.buildSelection(selectedUseTypes, selectedSpaceMitId, selectedGroupId);
        this.applySelection();
    }

    clearMap(){
        if(this.svg){
            this.svg.selectAll("*").remove();
            this.svg.attr("width", '100%').attr("height", '100%');
            
            this.svg.append("rect")
                .attr("width", "100%")
                .attr("height", "100%")
                .attr("fill", "transparent")
                .style("pointer-events", "all");
        }
    }

    // fill: fraction of the usable view the features should take up; maxScale caps the zoom level
    zoomToFeatures(features, { maxScale = 4, fill = 1 } = {}) {
        if (!features || features.length === 0 || !this.zoom || !this.svg) return;
        
        let fc = { type: "FeatureCollection", features: features };
        let bounds = this.path.bounds(fc);
        
        let dx = bounds[1][0] - bounds[0][0];
        let dy = bounds[1][1] - bounds[0][1];
        let x = (bounds[0][0] + bounds[1][0]) / 2;
        let y = (bounds[0][1] + bounds[1][1]) / 2;
        
        let node = this.svg.node();
        let width = node.getBoundingClientRect().width || window.innerWidth;
        let height = node.getBoundingClientRect().height || window.innerHeight;
        
        let isMobile = window.innerWidth <= 600;
        let paddingX = width * 0.2;
        let viewHeight = isMobile ? height * 0.45 : height * 0.8; 
        let viewCenterY = isMobile ? height * 0.35 : height / 2;
        
        let scaleX = (width - paddingX) * fill / dx;
        let scaleY = viewHeight * fill / dy;
        let scale = Math.max(1, Math.min(maxScale, Math.min(scaleX, scaleY)));
        
        let translate = [width / 2 - scale * x, viewCenterY - scale * y];
        
        this.svg.transition().duration(750).call(
            this.zoom.transform,
            d3.zoomIdentity.translate(translate[0], translate[1]).scale(scale)
        );
    }

    addUIObjectsDefinitions(objectList){
        let defs = this.svg.append("defs");

        defs.append("pattern")
            .attr("id", "conf-hatch")
            .attr("patternUnits", "userSpaceOnUse")
            // Smaller width/height makes the stripes closer together
            .attr("width", 6)
            .attr("height", 6)
            .append("path")
            // Drawing three segments ensures the diagonal lines connect perfectly at the tile edges
            .attr("d", "M-1,1 l2,-2 M0,6 l6,-6 M5,7 l2,-2")
            .attr("stroke", this.CONFERENCE_COLOR)
            .attr("stroke-width", 1.5) // Thinner stroke for a texture effect
            .attr("stroke-linecap", "square");

        for(let x = 0; x < objectList.length; x++){
            defs.append('pattern')
                .attr('id', objectList[x].type.replace(/ /g,"_"))
                .attr('width', 1)
                .attr('height', 1)
                .attr('patternContentUnits', 'objectBoundingBox')
                .append("image")
                    .attr("href", objectList[x].iconPath)
                    .attr('preserveAspectRatio', 'none')
                    .attr('width', 1)
                    .attr('height', 1);
        }
    }
    
    updateSpaceColor(space){
        if(!space || !space.properties) return;
        let d3Element = d3.select("#mit_" + space.properties.mitId);
        if(!d3Element.empty()){
            d3Element.attr("fill", this.getSpaceColor(space.properties));
        }
    }

    getSpaceColor(spaceProperties){
        if(spaceProperties.color) return spaceProperties.color;
        
        if (this.showNeighborhoods && this.groups) {
            let spaceGroups = this.groups.filter(g => g.spaceIds && g.spaceIds.includes(spaceProperties.mitId));
            if (spaceGroups.length === 1) return spaceGroups[0].color;
            if (spaceGroups.length > 1) return "url(#multi-group-" + spaceProperties.mitId + ")";
        }

        if(spaceProperties.commonArea) return this.colorCodes.CORRIDOR;
        if(spaceProperties.stairsElevator || spaceProperties.stairElevator) return this.colorCodes.ELEVATOR;
        return this.colorCodes[spaceProperties.useType] || "#D6D6D6";
    }

    getBaseSpaceOpacity(f){
        const useType = f.properties.useType ? f.properties.useType.toUpperCase() : '';
        const isConfRoom = useType === 'CONFERENCE ROOM' || useType === 'SEMINAR ROOM';

        if (this.showConferenceRooms && !isConfRoom && this.showNeighborhoods) {
            let spaceGroups = this.groups.filter(g => g.spaceIds && g.spaceIds.includes(f.properties.mitId));
            if (spaceGroups.length > 0) return this.CONFERENCE_SHOW_GROUP_DIM;
        }
        return 1.0;
    }

    getPreFloorSpaceColor(spaceProperties){ return this.colorCodes[spaceProperties.useType]; }

    addZoomEvents(){
        d3.selectAll(this.zoomControls.in).on("click", () => { this.svg.transition().call(this.zoom.scaleBy, 2); });
        d3.selectAll(this.zoomControls.out).on("click", () => { this.svg.transition().call(this.zoom.scaleBy, 0.5); });
        d3.selectAll(this.zoomControls.reset).on("click", () => { this.svg.transition().call(this.zoom.scaleTo, 1); });
    }

    dragStartEvent(event, space) {
        if(this.editModeAllowModify){
            space.dragControl = d3.select(event.sourceEvent.target).attr("cursor", "grabbing").attr("stroke", null);
            space.dragX = 0; space.dragY = 0
        }
    }
    
    dragEvent(event, space) {
        if(this.editModeAllowModify){
            space.properties.rotation = space.properties.rotation || 0;
            space.dragAccumulatedX = (space.dragAccumulatedX || 0) + event.dx;
            space.dragAccumulatedY = (space.dragAccumulatedY || 0) + event.dy;
            space.dragX += event.dx; space.dragY += event.dy;

            let centerPoint = this.path.centroid(space);
            space.dragControl.attr("transform", "translate(" + space.dragAccumulatedX + "," + space.dragAccumulatedY + ") rotate(" + space.properties.rotation + ", " + centerPoint[0] + ", " + centerPoint[1] + ")");
        }
    }

    dragEndEvent(event, space) {
        if(this.editModeAllowModify){
            space.dragControl.attr("cursor", "grab");

            if (space.properties.spaceType === 'nav_point') {
                let delta = { x: space.dragX || 0, y: space.dragY || 0 };
                let currentPt = this.projection(space.geometry.coordinates);
                let newPt = this.projection.invert([currentPt[0] + delta.x, currentPt[1] + delta.y]);
                space.geometry.coordinates = newPt;
                space.dragControl.attr("transform", null).attr("cx", this.projection(newPt)[0]).attr("cy", this.projection(newPt)[1]);
            } else {
                let parent = this.findParentSpace(event.x, event.y);
                let parentId = parent ? parent.properties.id : this.currentFloor.id;

                if(space.properties.parentId !== parentId){
                    delete this.existingObjectMitIdsList[space.properties.mitId];
                    let objInfo = this.getObjectInfo(this.existingObjectMitIdsList, space.properties.mitBuildingId, space.properties.useType, space.properties.name)
                    space.properties.parentId = parentId;
                    space.properties.mitId = objInfo.mitId;
                    space.properties.mitIdShort = objInfo.mitIdShort;
                    space.properties.name = objInfo.name; 
                    this.existingObjectMitIdsList[space.properties.mitId] = 1;
                }

                let delta = { x:space.dragX, y:space.dragY };
                let screenPoints = []
                let point = [];
                for(let x = 0; x < space.geometry.coordinates[0].length; x++){
                    point = this.projection(space.geometry.coordinates[0][x]);
                    point[0] += delta.x; point[1] += delta.y;
                    screenPoints.push(point);
                }

                let newPoints = []
                for(let x = 0; x < screenPoints.length; x++){
                    newPoints.push(this.projection.invert(screenPoints[x]));
                }
                space.geometry.coordinates = [newPoints];
                space.properties.geometryCoordinates = [newPoints];
            }

            let centerPoint = space.properties.spaceType === 'nav_point' ? this.projection(space.geometry.coordinates) : this.path.centroid(space);

            if (space.properties.spaceType !== 'nav_point') {
                space.dragControl.datum(space)
                                 .attr("d", (f) => this.path(f))
                                 .attr("transform", "rotate(" + space.properties.rotation + ", " + centerPoint[0] + ", " + centerPoint[1] + ")");
            }

            this.serviceSelection.selectApplyUI(space, space.dragControl);
            this.editObjectCallback(this.EDIT_OBJECT_TYPE.MODIFIED, space, space.dragControl);
            this.dragRemoveData(space);
        }
    }

    dragRemoveData(space){
        delete space.dragControl; delete space.dragX; delete space.dragY; delete space.dragAccumulatedX; delete space.dragAccumulatedY;
    }

    polygonContainsPoint(point, polygon) {
        var x = point[0], y = point[1];
        var inside = false;
        for (var i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
            var xi = polygon[i][0], yi = polygon[i][1];
            var xj = polygon[j][0], yj = polygon[j][1];

            var intersect = ((yi > y) != (yj > y)) && (x < (xj - xi) * (y - yi) / (yj - yi) + xi);
            if (intersect) inside = !inside;
        }
        return inside;
    }

    findParentSpace(mouseX,mouseY){
        let gisPoint = this.projection.invert([mouseX,mouseY]);
        let parent = null;
        this.layerSpaces.selectAll('path').each( (space, index) => {
            if(this.polygonContainsPoint(gisPoint, space.geometry.coordinates[0])){ parent = space; }
        });
        return parent;
    }

    createProjection(geojson, width, height){
        this.projection = d3.geoMercator().rotate([90, 28.5, 0]).fitSize([width, height], geojson);
        this.path = d3.geoPath().projection(this.projection);
    }

    createLayers(){
        // Keep the current zoom/pan when the map is redrawn
        this.layerMap = this.svg.append("g").attr("id", "group-map")
            .attr("transform", this.zoom ? d3.zoomTransform(this.svg.node()) : null);
        this.layerPrevSpaces = this.layerMap.append("g");
        this.layerSpaces = this.layerMap.append("g");
        this.layerStructure = this.layerMap.append("g");
        this.layerNavLines = this.layerMap.append("g");
        this.layerDoors = this.layerMap.append("g");
        this.layerWindows = this.layerMap.append("g");
        this.layerObjects = this.layerMap.append("g");
        this.layerSelection = this.layerMap.append("g").attr("id", "layer-selection").style("pointer-events", "none");
        this.layerHover = this.layerMap.append("g").attr("id", "layer-hover").style("pointer-events", "none");
        this.layerActiveRoute = this.layerMap.append("g"); 
        this.layerNavPoints = this.layerMap.append("g");
        this.layerText = this.layerMap.append("g");
    }

    renderLayerStructures(structureModel, selectedFloor){
        if(typeof structureModel === 'undefined' || structureModel === null || structureModel.length === 0) return;
        this.layerStructure.selectAll("line")
            .data(structureModel).enter().append("line")
            .attr("x1", d=>this.projection(d[0])[0]).attr("y1", d=>this.projection(d[0])[1])
            .attr("x2", d=>this.projection(d[1])[0]).attr("y2", d=>this.projection(d[1])[1])
            .attr("stroke", 'black').style("stroke-width", ".5px");
    }
    
    renderLayerDoors(doorsModel, selectedFloor, selectedUseTypes, selectedSpaceMitId, selectedOrg){
        if(typeof doorsModel === 'undefined' || doorsModel === null || doorsModel.features.length === 0) return;
        this.layerDoors.selectAll("path")
            .data(doorsModel.features).enter().append("path")
            .attr("d", (feature) => this.path(feature)).attr("id", (feature) => 'mit_'+feature.properties.mitId)
            .style("stroke-width", (f) => { return (f.properties.mitId == selectedSpaceMitId) ? "2px" : "1px" })
            .attr("stroke", (f) => {
                if(f.properties.mitId == selectedSpaceMitId || f.properties.useType == selectedUseTypes) return this.SELECTION_COLOR;
                return "#6D4A33"
            })
            .on("click", (event, obj) => {
                if(this.editmode) this.serviceSelection.select(obj, d3.select(event.target));
                else this.serviceSelection.selectEvent(obj, d3.select(event.target));
            })
            .on("mouseover", (event, obj) => {
                if(this.highlight) this.serviceSelection.highlight(obj, d3.select(event.target));
            })
            .on("mouseleave", (event, obj) => {
                if(this.highlight) this.serviceSelection.unhighlight(obj, d3.select(event.target));
            });
    }

    renderLayerWindows(windowsModel, selectedFloor, selectedUseTypes, selectedSpaceMitId, selectedOrg){
        if(typeof windowsModel === 'undefined' || windowsModel === null || windowsModel.length === 0) return;
        this.layerWindows.selectAll("line")
            .data(windowsModel).enter().append("line")
            .attr("x1", d=>this.projection(d[0])[0]).attr("y1", d=>this.projection(d[0])[1])
            .attr("x2", d=>this.projection(d[1])[0]).attr("y2", d=>this.projection(d[1])[1])
            .attr("stroke", (f) => { return (selectedUseTypes == 'WINDOW') ? this.SELECTION_COLOR : "#758CB3" })
            .style("stroke-width", ".5px");
    }

    renderLayerObjects(objectsModel, selectedFloor, selectedUseTypes, selectedSpaceMitId, selectedOrg){
        if(typeof objectsModel === 'undefined') return;
        this.layerObjects.selectAll("path")
            .data(objectsModel.features).enter().append("path")
            .attr("d", (feature) => { this.dragRemoveData(feature); return this.path(feature); })
            .attr("id", (feature) => 'mit_'+feature.properties.mitId)
            .attr("fill", (f) => { return "url(#"+f.properties.useType.replace(/ /g,"_")+")"; })
            .attr("transform", (f) => {
                let centerPoint = this.path.centroid(f);
                return "rotate(" + f.properties.rotation + ", " + centerPoint[0] + ", " + centerPoint[1] + ")";
            })
            .style("display", (f) => {
                if (this.showAllObjects) return null;
                if (selectedUseTypes && f.properties.useType === selectedUseTypes) return null;
                if (selectedSpaceMitId && f.properties.mitId === selectedSpaceMitId) return null;
                return "none";
            })
            .on("click", (event, obj) => {
                if(this.editmode) this.serviceSelection.select(obj, d3.select(event.target));
                else this.serviceSelection.selectEvent(obj, d3.select(event.target));
            })
            .call(d3.drag()
                .on("start", (event, space) => { this.dragStartEvent(event,space); })
                .on("drag", (event, space) => { this.dragEvent(event,space); })
                .on("end", (event, space) => { this.dragEndEvent(event,space); })
            );
    }

    renderLayerSpaces(spacesModel, selectedFloor, selectedUseTypes, selectedSpaceMitId, selectedOrg){
        this.svg.select("defs").selectAll(".multi-group-gradient").remove();

        if (this.showNeighborhoods && this.groups) {
            let defs = this.svg.select("defs");
            if (defs.empty()) defs = this.svg.append("defs");

            spacesModel.features.forEach(f => {
                let spaceGroups = this.groups.filter(g => g.spaceIds && g.spaceIds.includes(f.properties.mitId));
                if (spaceGroups.length > 1) {
                    let gradientId = "multi-group-" + f.properties.mitId;
                    let gradient = defs.append("linearGradient")
                        .attr("id", gradientId)
                        .attr("class", "multi-group-gradient")
                        .attr("x1", "0%").attr("y1", "0%")
                        .attr("x2", "100%").attr("y2", "100%"); 

                    let pctStep = 100 / spaceGroups.length;
                    spaceGroups.forEach((g, i) => {
                        gradient.append("stop").attr("offset", (i * pctStep) + "%").attr("stop-color", g.color);
                        gradient.append("stop").attr("offset", ((i + 1) * pctStep) + "%").attr("stop-color", g.color);
                    });
                }
            });
        }
        
        this.layerSpaces.selectAll("path")
            .data(spacesModel.features).enter().append("path")
            .attr("d", (feature) => this.path(feature)).attr("id", (feature) => 'mit_'+feature.properties.mitId)
            // --- FILL LOGIC ---
            // Base colors only; selection is drawn on top by applySelection()
            .attr("fill", (f) => {
                const useType = f.properties.useType ? f.properties.useType.toUpperCase() : '';
                const isConfRoom = useType === 'CONFERENCE ROOM' || useType === 'SEMINAR ROOM';

                if (this.showConferenceRooms && isConfRoom) {
                    return this.dimNeighborhoodsForConference ? this.CONFERENCE_COLOR : "url(#conf-hatch)";
                }

                if(this.getSpaceColor(f.properties)) return this.getSpaceColor(f.properties);
                else return (selectedOrg == null || f.properties.org == selectedOrg) ? 'white' : "#E8E8E8"
            })
            .style("opacity", (f) => this.getBaseSpaceOpacity(f))
            .style("stroke-width", "1px")
            .style("stroke", (f) => {
                const useType = f.properties.useType ? f.properties.useType.toUpperCase() : '';
                const isConfRoom = useType === 'CONFERENCE ROOM' || useType === 'SEMINAR ROOM';

                if (this.showConferenceRooms && isConfRoom) return this.CONFERENCE_COLOR;
                return "#ffffff";
            })
            .on("mouseover", (event, space) => {
                if(!this.highlight) return;
                this.previewIds([space.properties.mitId]);
                this.serviceSelection.highlight(space, d3.select(event.target));
            })
            .on("click",  (event, space) => {
                if(this.objectToCreate){
                    let m = d3.pointer(event);
                    this.addObject(this.objectToCreate, m[0], m[1], space);
                } else {
                    if(this.editmode) this.serviceSelection.select(space, d3.select(event.target));
                    else this.serviceSelection.selectEvent(space, d3.select(event.target));
                }
            })
            .on("mouseleave", (event, space) => {
                if(!this.highlight) return;
                this.previewIds(null);
                this.serviceSelection.unhighlight(space, d3.select(event.target));
            });

        let textData = spacesModel.features.filter((f) => {
            if(typeof f.properties === 'undefined')
                return false;

            if(selectedOrg && f.properties.org != selectedOrg)
                return false;

            if(f.properties.level === selectedFloor &&
                (
                    f.properties.useType == 'OFFICE' ||
                    f.properties.useType == 'LECTURE HALL'||
                    f.properties.useType == 'OFFICE SERVICE'||
                    f.properties.useType == 'RESEARCH LAB'||
                    f.properties.useType == 'RSCH LAB-OFFICE'||
                    f.properties.useType == 'LABORATORY SERVICE'||
                    f.properties.useType == 'LABORATORY SVC'||
                    f.properties.useType == 'SECY/RECEPTION'||
                    f.properties.useType == 'LAB SUPPORT SHOP'||
                    f.properties.useType == 'CLASSROOM'||
                    f.properties.useType == 'CLASSROOM SVC'||
                    f.properties.useType == 'COMPUTER FACIL'||
                    f.properties.useType == 'COMPUTER SERVICE'||
                    f.properties.useType == 'LOUNGE'||
                    f.properties.useType == 'CONFERENCE ROOM'||
                    f.properties.useType == 'STUDY ROOM'||
                    f.properties.useType == 'MULTI-PURPOSE RM' ||
                    f.properties.showRoomNumber
                )
            )
                return true;

            return false;
        });

        this.layerText.selectAll("text")
            .data(textData, d => d.properties.mitId).enter().append("svg:text")
            .text((d) => d.properties.mitIdShort )
            .attr("x", (d) => this.path.centroid(d)[0] )
            .attr("y", (d) => this.path.centroid(d)[1] )
            .attr("text-anchor","middle")
            .attr("dominant-baseline", "middle") 
            .attr('font-size', (this.roomNumberOptions.enabled ? this.roomNumberOptions.size : 4) + 'pt')
            .attr('font-weight', (this.roomNumberOptions.enabled && this.roomNumberOptions.bold) ? 'bold' : 'normal') 
            .attr('fill', (this.roomNumberOptions.enabled && this.roomNumberOptions.color) ? this.roomNumberOptions.color : '#000000') 
            .call(function getBB(selection) { selection.each(function(d){d.bbox = this.getBBox();}) });
            
        this.layerText.selectAll("rect")
            .data(textData, d => d.properties.mitId).enter().append("svg:rect")
            .attr("x", function(d){return d.bbox.x}).attr("y", function(d){return d.bbox.y})
            .attr("width", function(d){return d.bbox.width}).attr("height", function(d){return d.bbox.height})
            .attr("fill", (this.roomNumberOptions.enabled && this.roomNumberOptions.removeBg) ? "transparent" : "white");
            
        this.layerText.selectAll("text").raise()
            .call(function getBB(selection) { selection.each(function(d){delete d.bbox;}) });
    }
}

export default MitMapService;